import {useIsAuthenticated, useMsal} from "@azure/msal-react";
import {useLocation, useNavigate} from "react-router-dom";
import {MSAL_ALL_SCOPES, MSAL_AUTHORITY_SIGNIN, MSAL_AUTHORITY_SIGNUP} from "./msalConfig";
import {AppRoute, RoutePath} from "@router/RoutingModels";
import {buildRoute} from "@router/RouterUtils";
import {useAuthStore} from "@store/AuthStore";
import {useShallow} from "zustand/react/shallow";
import {authorityForPolicy} from "@msal/tokenService";
import {useQueryClient} from "@tanstack/react-query";
import {resetI18nOnLogout} from "@i18n/project/reset";
import {readUserLang} from "@i18n/language";
import {cleanupRejectedMsalAccount} from "@msal/msalUtils";

function clearAuthStorage() {
    console.log("[AUTH] clearAuthStorage");
    sessionStorage.removeItem("auth:returnUrl");

    for (const k of Object.keys(localStorage)) {
        if (k.startsWith("msal.") || k.includes("msal")) localStorage.removeItem(k);
    }
}

function resetAuthStore() {
    console.log("[AUTH] resetAuthStore");
    useAuthStore.setState({
        loading: true,
        overlayVisible: false,
        overlayMessage: null,
        isLoggingOut: false,
        isAuthenticated: false,
        account: null,
        idTokenClaims: null,
        accessToken: null,
        derivedCountry: null,
        postLoginRedirectPath: null,
    });
}

export function useAuth() {

    const uiLang = readUserLang();

    const queryClient = useQueryClient();
    const {instance} = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const navigate = useNavigate();
    const location = useLocation();

    const {setOverlay, setPostLoginRedirect} = useAuthStore(
        useShallow((s) => ({
            setOverlay: s.setOverlay,
            setPostLoginRedirect: s.setPostLoginRedirect,
        }))
    );

    const account = instance.getActiveAccount() || null;
    const authority = instance.getConfiguration().auth.authority;

    function saveReturnUrl() {
        const target = location.pathname + location.search + location.hash;
        setPostLoginRedirect(target);
    }

    function getLoginPrompt(): "login" | "select_account" {
        return instance.getActiveAccount() ? "select_account" : "login";
    }

    async function loginRedirect(flow: "SIGNIN" | "SIGNUP" = "SIGNIN") {
        console.log("[AUTH] loginRedirect", {flow, prompt: getLoginPrompt()});
        await cleanupRejectedMsalAccount();
        saveReturnUrl();
        setOverlay(true);

        await instance.loginRedirect({
            scopes: MSAL_ALL_SCOPES,
            authority: flow === "SIGNIN" ? MSAL_AUTHORITY_SIGNIN : MSAL_AUTHORITY_SIGNUP,
            state: sessionStorage.getItem("auth:returnUrl") || RoutePath.HOME,
            prompt: getLoginPrompt(),
            extraQueryParameters: {
                ui_locales: uiLang,
            }
        });
    }

    async function logout() {
        try {
            console.log("[AUTH] logout");
            await cleanupRejectedMsalAccount();
            setOverlay(true);
            resetI18nOnLogout(queryClient);
            useAuthStore.setState({isLoggingOut: true});

            const acc = instance.getActiveAccount();
            const policy = acc ? ((acc.idTokenClaims as any)?.tfp || (acc.idTokenClaims as any)?.acr) : null;

            await instance.logoutRedirect({authority: authorityForPolicy(policy)});
        } catch (e) {
            console.log("[AUTH] logout error", e);
            setOverlay(false);
            useAuthStore.setState({isLoggingOut: false});
            navigate(buildRoute(AppRoute.WELCOME), {replace: true});
        }
    }

    async function logoutFull(opts?: { postLogoutRedirectToOpsReason?: string }) {
        try {
            console.log("[AUTH] logoutFull");
            setOverlay(true);
            resetI18nOnLogout(queryClient);
            useAuthStore.setState({ isLoggingOut: true });

            const acc = instance.getActiveAccount();
            const policy = acc ? ((acc.idTokenClaims as any)?.tfp || (acc.idTokenClaims as any)?.acr) : null;

            const postLogoutRedirectUri = opts?.postLogoutRedirectToOpsReason
                ? `${window.location.origin}${buildRoute(AppRoute.OPS)}?reason=${encodeURIComponent(
                    opts.postLogoutRedirectToOpsReason
                )}`
                : undefined;

            await instance.logoutRedirect({
                authority: authorityForPolicy(policy),
                postLogoutRedirectUri,
                onRedirectNavigate: () => {
                    console.log("[AUTH] logoutFull onRedirectNavigate");
                    try {
                        instance.setActiveAccount(null);
                    } catch {
                    }
                    resetAuthStore();
                    clearAuthStorage();
                    return true;
                },
            });
        } catch (e) {
            console.log("[AUTH] logoutFull error", e);
            resetAuthStore();
            clearAuthStorage();
            setOverlay(false);
            useAuthStore.setState({isLoggingOut: false});

            if (opts?.postLogoutRedirectToOpsReason) {
                navigate(
                    `${buildRoute(AppRoute.OPS)}?reason=${encodeURIComponent(opts.postLogoutRedirectToOpsReason)}`,
                    { replace: true }
                );
                return;
            }

            navigate(buildRoute(AppRoute.WELCOME), {replace: true});
        }
    }

    return {isAuthenticated, account, authority, loginRedirect, logout, logoutFull};
}
